/** Role used as the user's primary identity (group-priority pick). */
export type PrimaryRoleIdentity = {
  code: string;
  label: string;
  roleGroup?: string;
};

/** Minimal role shape accepted when resolving a primary identity. */
type RoleLike = {
  code?: string;
  label?: string;
  roleGroup?: string;
};

/** Role-group pick order for the user's primary identity. */
const GROUP_PRIORITY = ['admins', 'volunteers', 'tutors', 'students', 'parents'] as const;

/** First role whose group appears in {@link GROUP_PRIORITY}, else the first role. */
function pickRoleByGroupPriority(roles: PrimaryRoleIdentity[]): PrimaryRoleIdentity | null {
  if (roles.length === 0) return null;
  for (const group of GROUP_PRIORITY) {
    const match = roles.find((role) => role.roleGroup === group);
    if (match) return match;
  }
  return roles[0];
}

/** Map a loose role object to a primary-identity record, or null without a code. */
function toPrimaryRoleIdentity(role: RoleLike | undefined): PrimaryRoleIdentity | null {
  if (!role?.code) return null;
  return {
    code: role.code,
    label: role.label ?? role.code,
    roleGroup: role.roleGroup,
  };
}

/** Primary role from nested `userRoles[].role`. */
export function getPrimaryRole(
  userRoles?: Array<{ role?: RoleLike }> | null,
): PrimaryRoleIdentity | null {
  const roles: PrimaryRoleIdentity[] = [];
  for (const userRole of userRoles ?? []) {
    const identity = toPrimaryRoleIdentity(userRole.role);
    if (identity) roles.push(identity);
  }
  return pickRoleByGroupPriority(roles);
}

/** Primary role from a flat auth roles array. */
export function getPrimaryAuthRole(roles?: RoleLike[] | null): PrimaryRoleIdentity | null {
  const identities: PrimaryRoleIdentity[] = [];
  for (const role of roles ?? []) {
    const identity = toPrimaryRoleIdentity(role);
    if (identity) identities.push(identity);
  }
  return pickRoleByGroupPriority(identities);
}
